import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PrintIcon from '@mui/icons-material/Print';

import { PermissionGuard } from '../../../../auth/components/PermissionGuard';
import { ParteProduccionService } from '../../partes-produccion/services/ParteProduccionService';
import { PrecioClienteService } from '../../precios-cliente/services/precioClienteService';
import { OrdenFacturacionService } from '../services/ordenFacturacionService';

import type { ParteProduccionDetailResponse } from '../../partes-produccion/types';
import type { PrecioCliente } from '../../precios-cliente/types';
import type { OrdenFacturacion, EstadoType } from '../types';
import { estadoLabels } from '../types';
import { ordenFacturacionPdfService } from '../services/ordenFacturacionPdfService';

// --- Constants ---

const TASA_DETRACCION = 0.015;
const TASA_IGV = 0.18;
const TASA_DETRACCION_CLIENTE = 0.12;

// --- Helper types ---

interface DetalleAgrupado {
  presentacion: string;
  calibre: string;
  kilos: number;
  precioMP: number;
  montoMP: number;
  precioServicio: number;
  montoServicio: number;
  precioEncontrado: boolean;
  idPresentacion?: number;
  idCalibre?: number;
}

// --- Component ---

const OrdenFacturacionFormPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const parteId = Number(searchParams.get('parteId') || 0);

  // Data state
  const [parteDetail, setParteDetail] = useState<ParteProduccionDetailResponse | null>(null);
  const [precios, setPrecios] = useState<PrecioCliente[]>([]);

  // Form state
  const [tipoProceso, setTipoProceso] = useState<string>(searchParams.get('tipoProceso') || '');

  // Inline price edits
  const [precioEdits, setPrecioEdits] = useState<Record<string, { precioMP: number; precioServicio: number }>>({});

  // Existing order state
  const [ordenExistente, setOrdenExistente] = useState<OrdenFacturacion | null>(null);
  const [updatingEstado, setUpdatingEstado] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Loading / errors
  const [loadingParte, setLoadingParte] = useState(false);
  const [loadingPrecios, setLoadingPrecios] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preciosLoaded, setPreciosLoaded] = useState(false);

  // Read-only when order exists and is not pending
  const isReadOnly = ordenExistente != null && ordenExistente.estado !== 'PEN';

  // Refs for preventing reset on initial render
  const initialTipoProceso = React.useRef(searchParams.get('tipoProceso') || '');
  const isFirstRender = React.useRef(true);

  // --- Load parte data and existing order info ---

  useEffect(() => {
    if (!parteId) return;

    const loadParte = async () => {
      setLoadingParte(true);
      setError(null);
      try {
        const detail = await ParteProduccionService.getById(parteId);
        setParteDetail(detail);

        // Try to load existing order for this parte
        try {
          const ordenes = await OrdenFacturacionService.getAll();
          const orden = ordenes.find((o) => o.idParte === parteId);
          if (orden) {
            setOrdenExistente(orden);
            if (!searchParams.get('tipoProceso')) {
              setTipoProceso(orden.tipoProceso);
              initialTipoProceso.current = orden.tipoProceso;
            }
          }
        } catch {
          // No existing order, that's fine
        }
      } catch {
        setError('Error al cargar los datos del parte de producción.');
      } finally {
        setLoadingParte(false);
      }
    };

    loadParte();
  }, [parteId, searchParams]);

  // --- Load prices when tipoProceso selected ---

  const idCliente = parteDetail?.idCliente;

  const loadPrecios = useCallback(async () => {
    if (!idCliente || !tipoProceso) return;

    setLoadingPrecios(true);
    setPreciosLoaded(false);
    setPrecioEdits({});
    try {
      const result = await PrecioClienteService.getByClienteAndTipo(
        idCliente,
        tipoProceso
      );
      setPrecios(result);

      // Pre-populate edits with existing prices
      const edits: Record<string, { precioMP: number; precioServicio: number }> = {};
      for (const p of result) {
        const pres = p.presentacion?.descripcion || '';
        const cal = p.calibre?.descripcion || '';
        if (pres && cal) {
          edits[`${pres}|${cal}`] = {
            precioMP: p.precioMateriaPrima,
            precioServicio: p.precioServicio,
          };
        }
      }
      setPrecioEdits(edits);

      setPreciosLoaded(true);
    } catch {
      setPrecios([]);
      setPreciosLoaded(true);
    } finally {
      setLoadingPrecios(false);
    }
  }, [idCliente, tipoProceso]);

  useEffect(() => {
    if (tipoProceso && idCliente) {
      loadPrecios();
    }
  }, [tipoProceso, idCliente, loadPrecios]);

  // Reset prices when tipoProceso changes (skip if pre-populated)
  useEffect(() => {
    if (isFirstRender.current && tipoProceso === initialTipoProceso.current) {
      isFirstRender.current = false;
      return;
    }
    isFirstRender.current = false;
    setPrecios([]);
    setPreciosLoaded(false);
    setPrecioEdits({});
  }, [tipoProceso]);

  // --- Match prices to detalles ---

  const findPrecio = useCallback(
    (presentacion: string, calibre: string): PrecioCliente | undefined => {
      return precios.find(
        (p) =>
          p.presentacion?.descripcion?.toLowerCase() === presentacion.toLowerCase() &&
          p.calibre?.descripcion?.toLowerCase() === calibre.toLowerCase()
      );
    },
    [precios]
  );

  // --- Group detalles from parte's productos terminados ---

  const detallesAgrupados = useMemo((): DetalleAgrupado[] => {
    if (!parteDetail?.productosTerminados) return [];

    const groups = new Map<string, { kilos: number; idPresentacion?: number; idCalibre?: number }>();

    for (const grupo of parteDetail.productosTerminados) {
      for (const item of grupo.items) {
        // Skip devoluciones internas
        if (item.devolucionInterna) continue;

        const pres = item.presentacion || 'Sin Presentacion';
        const cal = item.calibre || 'Sin Calibre';
        const key = `${pres}|${cal}`;

        const kilos = item.total || 0;

        const existing = groups.get(key);
        if (existing) {
          existing.kilos += kilos;
        } else {
          groups.set(key, { kilos, idPresentacion: item.idPresentacion, idCalibre: item.idCalibre });
        }
      }
    }

    const result: DetalleAgrupado[] = [];

    for (const [key, group] of groups) {
      const [pres, cal] = key.split('|');
      const precio = findPrecio(pres, cal);
      const edit = precioEdits[key];

      const precioMP = edit?.precioMP ?? precio?.precioMateriaPrima ?? 0;
      const precioServ = edit?.precioServicio ?? precio?.precioServicio ?? 0;

      result.push({
        presentacion: pres,
        calibre: cal,
        kilos: group.kilos,
        precioMP,
        montoMP: group.kilos * precioMP,
        precioServicio: precioServ,
        montoServicio: group.kilos * precioServ,
        precioEncontrado: !!precio,
        idPresentacion: precio?.idPresentacion || group.idPresentacion,
        idCalibre: precio?.idCalibre || group.idCalibre,
      });
    }

    return result;
  }, [parteDetail, findPrecio, precioEdits]);

  // --- Calculations ---

  const subTotal = useMemo(
    () => detallesAgrupados.reduce((sum, d) => sum + d.montoMP, 0),
    [detallesAgrupados]
  );

  const detraccion = subTotal * TASA_DETRACCION;

  const totalFacturarMP = subTotal;

  const subTotalServicio = useMemo(
    () => detallesAgrupados.reduce((sum, d) => sum + d.montoServicio, 0),
    [detallesAgrupados]
  );

  const igv = subTotalServicio * TASA_IGV;

  const totalServicioConIgv = subTotalServicio + igv;

  const detraccionCliente = totalServicioConIgv * TASA_DETRACCION_CLIENTE;

  const cuotaUnica = totalServicioConIgv - detraccionCliente;

  // --- Check if there are missing prices ---

  const todosLosPreciosCompletos = useMemo(
    () => preciosLoaded && detallesAgrupados.length > 0 && detallesAgrupados.every((d) => d.precioMP > 0),
    [detallesAgrupados, preciosLoaded]
  );

  const totalPositivo = subTotal > 0;

  // --- Inline price edit handlers ---

  const handlePrecioEdit = (key: string, field: 'precioMP' | 'precioServicio', value: string) => {
    const numValue = parseFloat(value) || 0;
    setPrecioEdits((prev) => ({
      ...prev,
      [key]: {
        precioMP: prev[key]?.precioMP ?? 0,
        precioServicio: prev[key]?.precioServicio ?? 0,
        [field]: numValue,
      },
    }));
  };

  // --- Generate orden (auto-saves prices first) ---

  const handleGenerarOrden = async () => {
    if (!parteId || !idCliente || !tipoProceso) return;

    if (!todosLosPreciosCompletos) {
      setError('Debe ingresar todos los precios de materia prima antes de generar la orden.');
      return;
    }

    if (!totalPositivo) {
      setError('El subtotal debe ser positivo.');
      return;
    }

    setGenerando(true);
    setError(null);
    try {
      // Auto-save prices to PrecioCliente
      const preciosToSave: PrecioCliente[] = [];
      for (const det of detallesAgrupados) {
        const key = `${det.presentacion}|${det.calibre}`;
        const edit = precioEdits[key];
        if (!edit || (edit.precioMP === 0 && edit.precioServicio === 0)) continue;

        const existingPrecio = findPrecio(det.presentacion, det.calibre);
        preciosToSave.push({
          id: existingPrecio?.id || 0,
          idCliente,
          idPresentacion: existingPrecio?.idPresentacion || det.idPresentacion || 0,
          idCalibre: existingPrecio?.idCalibre || det.idCalibre || 0,
          tipoProceso,
          precioMateriaPrima: edit.precioMP,
          precioServicio: edit.precioServicio,
          fechaActualizacion: new Date().toISOString(),
          activo: true,
        });
      }

      if (preciosToSave.length > 0) {
        await PrecioClienteService.saveBulk({
          idCliente,
          tipoProceso,
          precios: preciosToSave,
        });
      }

      // Generate the order
      await OrdenFacturacionService.generar(parteId);
      navigate('/administracion/orden-facturacion');
    } catch {
      setError('Error al generar la orden de facturación.');
    } finally {
      setGenerando(false);
    }
  };

  // --- Update estado ---

  const handleEstadoChange = async (event: SelectChangeEvent) => {
    const nuevoEstado = event.target.value as EstadoType;
    if (!ordenExistente) return;
    setUpdatingEstado(true);
    setError(null);
    try {
      await OrdenFacturacionService.updateEstado(ordenExistente.id, nuevoEstado);
      setOrdenExistente({ ...ordenExistente, estado: nuevoEstado });
      setSuccessMsg(`Estado actualizado a ${estadoLabels[nuevoEstado]}`);

      setTimeout(() => {
        navigate('/administracion/orden-facturacion');
      }, 1500);
    } catch {
      setError(`Error al cambiar el estado de la orden.`);
      setUpdatingEstado(false);
    }
  };

  // --- Print PDF ---

  const handlePrint = () => {
    if (!ordenExistente || !parteDetail) return;

    try {
      ordenFacturacionPdfService.generatePdf({
        numero: ordenExistente.numero,
        fecha: ordenExistente.fechaProceso,
        numeroDocumento: parteDetail.derc?.numeroDocumento || '',
        cliente: parteDetail.clienteRazonSocial || '',
        numeroParte: parteDetail.numeroComprobante || 0,
        responsable: parteDetail.responsable || '',
        mallas: parteDetail.derc?.numeroMallas || 0,
        cajas: parteDetail.cajas || 0,
        tipoProceso,
        detalles: detallesAgrupados.map((d) => ({
          presentacion: d.presentacion,
          calibre: d.calibre,
          kilos: d.kilos,
          precioMP: d.precioMP,
          montoMP: d.montoMP,
          precioServicio: d.precioServicio,
          montoServicio: d.montoServicio,
        })),
        totalKilos: detallesAgrupados.reduce((s, d) => s + d.kilos, 0),
        subTotal,
        detraccion,
        totalFacturarMP,
        subTotalServicio,
        igv,
        totalServicioConIgv,
        detraccionCliente,
        cuotaUnica,
      });
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Error al generar el PDF.');
    }
  };

  // --- Render helpers ---

  const formatNumber = (n: number, decimals = 2) =>
    n.toLocaleString('es-PE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const formatCurrency = (n: number) => `S/ ${formatNumber(n)}`;

  // --- Loading state ---

  if (loadingParte) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!parteId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No se especificó el ID del parte de producción.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/produccion/partes-produccion')}
          sx={{ mt: 2 }}
        >
          Volver a Partes de Producción
        </Button>
      </Box>
    );
  }

  // Group detalles by presentacion for display
  const presentaciones = [...new Set(detallesAgrupados.map((d) => d.presentacion))];

  return (
    <Box sx={{ p: 2, width: '100%', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/administracion/orden-facturacion')}
            sx={{ mr: 2 }}
          >
            Volver
          </Button>
          <Typography variant="h5" component="h1" fontWeight={600} sx={{ flex: 1 }}>
            {ordenExistente ? `Orden de Facturación N° ${ordenExistente.numero}` : 'Nueva Orden de Facturación'}
          </Typography>
          {ordenExistente && preciosLoaded && detallesAgrupados.length > 0 && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ mr: 2 }}
            >
              Imprimir PDF
            </Button>
          )}
        </Box>

        {/* Estado section */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label="FACTURACIÓN" color="info" size="medium" variant="filled" sx={{ fontWeight: 600 }} />
          {ordenExistente && (
            <PermissionGuard permission="FACTURACION_APROBAR" fallback={
              <Chip label={estadoLabels[ordenExistente.estado as EstadoType] || ordenExistente.estado} size="small" />
            }>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Estado:</Typography>
              <Select
                value={ordenExistente.estado}
                onChange={handleEstadoChange}
                size="small"
                sx={{ minWidth: 150 }}
                disabled={updatingEstado}
              >
                <MenuItem value="PEN">
                  <Chip label="Pendiente" color="warning" size="small" />
                </MenuItem>
                <MenuItem value="APR">
                  <Chip label="Aprobado" color="success" size="small" />
                </MenuItem>
                <MenuItem value="ANU">
                  <Chip label="Anulado" color="error" size="small" />
                </MenuItem>
              </Select>
              {updatingEstado && <CircularProgress size={20} />}
            </Box>
            </PermissionGuard>
          )}
        </Box>

        {successMsg && (
          <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 3 }}>
            {successMsg}
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Parte Info */}
        {parteDetail && (
        <Paper variant="outlined" sx={{ mb: 2, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: 'grey.100', px: 3, py: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Información del Parte de Producción
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                N° Parte
              </Typography>
              <Typography variant="body2">{parteDetail.numeroComprobante || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Fecha
              </Typography>
              <Typography variant="body2">
                {parteDetail.fecha ? new Date(parteDetail.fecha).toLocaleDateString('es-ES') : '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Cliente
              </Typography>
              <Typography variant="body2">
                {parteDetail.clienteRazonSocial || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                N° DERC
              </Typography>
              <Typography variant="body2">
                {parteDetail.derc?.numeroDocumento || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Responsable
              </Typography>
              <Typography variant="body2">
                {parteDetail.responsable || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Cajas
              </Typography>
              <Typography variant="body2">{parteDetail.cajas ?? '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Mallas
              </Typography>
              <Typography variant="body2">{parteDetail.derc?.numeroMallas ?? '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Estado
              </Typography>
              <Typography variant="body2">
                <Chip label={parteDetail.estado || 'N/A'} size="small" />
              </Typography>
            </Box>
          </Box>
          </Box>
        </Paper>
        )}

        {/* Tipo Proceso */}
        <Paper variant="outlined" sx={{ mb: 2, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <Box sx={{ backgroundColor: 'grey.100', px: 3, py: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Configuración de Facturación
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }} size="small" disabled={isReadOnly}>
            <InputLabel>Tipo Proceso</InputLabel>
            <Select
              value={tipoProceso}
              label="Tipo Proceso"
              onChange={(e) => setTipoProceso(e.target.value)}
            >
              <MenuItem value="">
                <em>Seleccionar</em>
              </MenuItem>
              <MenuItem value="COMPRA">Compra</MenuItem>
              <MenuItem value="SERVICIO">Servicio</MenuItem>
            </Select>
          </FormControl>

          {loadingPrecios && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
        </Box>

          {!idCliente && parteDetail && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No se pudo determinar el cliente de este parte. Es posible que no se pueda cargar los
              precios.
            </Alert>
          )}
          </Box>
        </Paper>

        {/* Missing prices alert */}
      {preciosLoaded && !todosLosPreciosCompletos && detallesAgrupados.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Ingrese todos los precios de materia prima para poder generar la orden. Los precios se
          guardarán automáticamente en la lista de precios del cliente.
        </Alert>
      )}

      {/* Detalles Table */}
      {preciosLoaded && detallesAgrupados.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Presentación</TableCell>
                  <TableCell>Calibre</TableCell>
                  <TableCell align="right">Kilos</TableCell>
                  <TableCell align="right">Precio MP</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell align="right">Precio Servicio</TableCell>
                  <TableCell align="right">Monto Servicio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {presentaciones.map((pres) => {
                  const items = detallesAgrupados.filter((d) => d.presentacion === pres);
                  return items.map((det, idx) => {
                    const key = `${det.presentacion}|${det.calibre}`;
                    return (
                      <TableRow
                        key={key}
                        sx={{
                          bgcolor: !det.precioEncontrado ? 'warning.50' : undefined,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <TableCell>
                          {idx === 0 ? (
                            <Typography variant="body2" fontWeight={600}>
                              {det.presentacion}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{det.calibre}</TableCell>
                        <TableCell align="right">{formatNumber(det.kilos)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            variant="outlined"
                            value={precioEdits[key]?.precioMP ?? ''}
                            onChange={(e) => handlePrecioEdit(key, 'precioMP', e.target.value)}
                            sx={{ width: 110 }}
                            inputProps={{ min: 0, step: 0.01 }}
                            disabled={isReadOnly}
                          />
                        </TableCell>
                        <TableCell align="right">{formatCurrency(det.montoMP)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            variant="outlined"
                            value={precioEdits[key]?.precioServicio ?? ''}
                            onChange={(e) =>
                              handlePrecioEdit(key, 'precioServicio', e.target.value)
                            }
                            sx={{ width: 110 }}
                            inputProps={{ min: 0, step: 0.01 }}
                            disabled={isReadOnly}
                          />
                        </TableCell>
                        <TableCell align="right">{formatCurrency(det.montoServicio)}</TableCell>
                      </TableRow>
                    );
                  });
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={7}
                    sx={{ borderBottom: 'none', pt: 2 }}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Totals - Resumen de Facturación */}
      {preciosLoaded && detallesAgrupados.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Resumen de Facturación
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
              maxWidth: 500,
              ml: 'auto',
            }}
          >
            {/* Materia Prima section */}
            <Typography variant="body2" fontWeight={600} sx={{ gridColumn: '1 / -1', mt: 1 }}>
              Materia Prima
            </Typography>

            <Typography variant="body2">SubTotal (MP):</Typography>
            <Typography variant="body2" align="right" fontWeight={500}>
              {formatCurrency(subTotal)}
            </Typography>

            <Typography variant="body2">Detracción (1.5%):</Typography>
            <Typography variant="body2" align="right" color="error.main">
              - {formatCurrency(detraccion)}
            </Typography>

            <Typography variant="body2">Total Facturar (MP):</Typography>
            <Typography variant="body2" align="right" fontWeight={600}>
              {formatCurrency(totalFacturarMP)}
            </Typography>

            <Divider sx={{ gridColumn: '1 / -1', my: 1 }} />

            {/* Servicio section */}
            <Typography variant="body2" fontWeight={600} sx={{ gridColumn: '1 / -1' }}>
              Servicio
            </Typography>

            <Typography variant="body2">SubTotal Servicio:</Typography>
            <Typography variant="body2" align="right" fontWeight={500}>
              {formatCurrency(subTotalServicio)}
            </Typography>

            <Typography variant="body2">IGV (18%):</Typography>
            <Typography variant="body2" align="right">
              {formatCurrency(igv)}
            </Typography>

            <Typography variant="body2">Total Servicio con IGV:</Typography>
            <Typography variant="body2" align="right" fontWeight={500}>
              {formatCurrency(totalServicioConIgv)}
            </Typography>

            <Typography variant="body2">Detracción Cliente (12%):</Typography>
            <Typography variant="body2" align="right" color="error.main">
              - {formatCurrency(detraccionCliente)}
            </Typography>

            <Divider sx={{ gridColumn: '1 / -1', my: 1 }} />

            <Typography variant="subtitle1" fontWeight={700}>
              Cuota Única:
            </Typography>
            <Typography variant="subtitle1" align="right" fontWeight={700} color="primary.main">
              {formatCurrency(cuotaUnica)}
            </Typography>
          </Box>
        </Paper>
      )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/administracion/orden-facturacion')}
          >
            Cancelar
          </Button>

          {!isReadOnly && (
            <Button
              variant="contained"
              color="primary"
              startIcon={generando ? <CircularProgress size={20} color="inherit" /> : <ReceiptLongIcon />}
              onClick={handleGenerarOrden}
              disabled={
                generando ||
                !tipoProceso ||
                !preciosLoaded ||
                detallesAgrupados.length === 0 ||
                !todosLosPreciosCompletos ||
                !totalPositivo
              }
            >
              {generando ? 'Generando...' : 'Generar Orden'}
            </Button>
          )}
        </Box>

      </Paper>
    </Box>
  );
};

export default OrdenFacturacionFormPage;
